import { Filters, IPageFilter, managePageFiltersMultiple } from '../../../utils/urlUtil'
import CheckboxView from '../../../components/CheckboxView/checkboxView'
import { FilterValuesData } from '../filters'
import PopupInfo from '../../../components/PopupInfo/popupInfo'
import React, { useEffect, useMemo, useState } from 'react'
import { Input } from 'semantic-ui-react'
import {
  CaretDown as SvgCaretDown,
  CaretRight as SvgCaretRight,
  MagnifyingGlass as SvgMagnifyingGlass
} from 'phosphor-react'
import { useIntl } from 'react-intl'

interface IProps {
  filterPage: string
  filters: Filters
  filterKeyParent: string
  filterKeyChild: string
  filterTitleI18NId: string
  filterOptionsData: FilterValuesData[]
  titleGroup?: string
  tooltip?: string
  allowParentSelection?: boolean
  disabledTooltip?: string
  closeFilters?: () => void
  changeFunction?: (key: string, setValue?: string | string[]) => void
}

const FilterGrouped = (props: IProps): React.ReactElement => {
  const {
    filterPage,
    closeFilters,
    filters,
    filterKeyParent,
    filterKeyChild,
    filterTitleI18NId,
    titleGroup,
    filterOptionsData,
    tooltip,
    disabledTooltip = '',
    allowParentSelection = true
  } = props

  const currentParentFilter = filters[filterKeyParent] || []
  const currentChildrenFilter = filters[filterKeyChild] || []
  const intl = useIntl()

  type Group = {
    groupId: string
    groupName?: string
    groupDisabled?: boolean
    values: Array<{ id: string; name?: string }>
  }

  const groupsMemo = useMemo(() => {
    const groups: { [key: string]: Group } = {}

    filterOptionsData.forEach((opt) => {
      if (opt.groups) {
        opt.groups?.forEach((gr) => {
          const groupId = gr.groupId || ''

          groups[groupId] = {
            groupId: groupId,
            groupName: gr.groupName || '',
            groupDisabled: !!gr.groupDisabled,
            values: [...(groups[groupId]?.values || []), { id: opt.key, name: opt.name }]
          }
        })
      } else {
        groups[opt.key] = {
          groupId: opt.key,
          groupName: opt.name || '',
          values: []
        }
      }
    })

    return Object.values(groups)
  }, [filterOptionsData])

  const [groups, setGroups] = useState<Group[]>([])
  const [expandedGroups, setExpandedGroups] = useState<string[]>([])

  useEffect(() => {
    setGroups(groupsMemo)
  }, [groupsMemo])

  // search
  const [searchValue, setSearchValue] = useState('')
  const handleSearch = (_, { value }) => {
    setSearchValue(value)

    if (!value) {
      setExpandedGroups([])
      setGroups(groupsMemo)
      return
    }

    const val = value.toLowerCase()

    const foundGroups = groupsMemo.filter((gr) => {
      return !!gr.values.find((opt) => (opt.name || opt.id).toLowerCase().includes(val))
    })
    setExpandedGroups(foundGroups.map((gr) => gr.groupId))
    setGroups(foundGroups)
  }
  const searchPlaceholder = intl.formatMessage({ id: filterTitleI18NId })

  return (
    <div className="column filters-grouped" data-test-id="filters-filters-grouped">
      <div className="title">
        <Input
          value={searchValue}
          onChange={handleSearch}
          input={{ className: 'truncate', title: searchPlaceholder }}
          placeholder={searchPlaceholder}
          icon={
            <div className="icon-container">
              <SvgMagnifyingGlass size={20} color="var(--color-river-bed)" />
            </div>
          }
        />
        {tooltip && <PopupInfo position="bottom center" text={tooltip} />}
      </div>
      <div className="filters-list custom-scrollbar">
        {groups.map((group) => {
          const childrenIds = group.values.map((val) => val.id)
          const currentlySelectedGroupChildrenIds = group.values
            .filter((child) => currentChildrenFilter.includes(child.id))
            .map((child) => child.id)

          const isChecked =
            currentParentFilter.includes(group.groupId) ||
            !!(
              currentChildrenFilter &&
              currentChildrenFilter?.find((childFilter) => childrenIds.includes(childFilter))
            )

          const toggleFilter = () => {
            const childrenValues = (currentChildrenFilter || []).filter(
              (urlId) => !currentlySelectedGroupChildrenIds.includes(urlId)
            )
            managePageFiltersMultiple({
              pageName: filterPage,
              filters: [
                {
                  filterKey: filterKeyParent,
                  filterValues: [group.groupId],
                  action: isChecked ? 'remove' : 'add'
                },
                {
                  filterKey: filterKeyChild,
                  filterValues: childrenValues,
                  action: childrenValues.length > 0 ? 'replace' : 'remove'
                }
              ]
            })
            closeFilters && closeFilters()
          }

          const isExpanded = expandedGroups.includes(group.groupId)
          const toggleExpand = () => {
            setExpandedGroups(
              isExpanded
                ? expandedGroups.filter((key) => key !== group.groupId)
                : [...expandedGroups, group.groupId]
            )
          }

          const filteredChildren = group.values.filter((opt) => {
            return !!(opt.name || opt.id).toLowerCase().includes(searchValue)
          })

          return (
            <div className="filters-nested" key={group.groupId}>
              <div className="toggle-row">
                <button className="btn caret icon-box" onClick={toggleExpand}>
                  {group.values?.length ? (
                    isExpanded ? (
                      <SvgCaretDown size={12} weight="bold" color="#5D6BCE" />
                    ) : (
                      <SvgCaretRight size={12} weight="bold" color="#5D6BCE" />
                    )
                  ) : (
                    <></>
                  )}
                </button>
                <div className="parent-filter-wrapper">
                  {allowParentSelection ? (
                    <CheckboxView
                      disabled={group.groupDisabled}
                      content={
                        <div className="checkbox-content">
                          <div className="name">{group.groupName || group.groupId}</div>
                          {currentlySelectedGroupChildrenIds.length ? (
                            <div className="child-amount">
                              ({currentlySelectedGroupChildrenIds.length}/{group.values.length}
                              {!!titleGroup && <>&nbsp;{intl.formatMessage({ id: titleGroup })}</>})
                            </div>
                          ) : group.values?.length ? (
                            <div className="child-amount">
                              ({group.values.length}
                              {!!titleGroup && <>&nbsp;{intl.formatMessage({ id: titleGroup })}</>})
                            </div>
                          ) : (
                            <></>
                          )}
                        </div>
                      }
                      checked={isChecked}
                      onChange={toggleFilter}
                    />
                  ) : (
                    <div className="checkbox-content">
                      <div className="name">{group.groupName || group.groupId}</div>
                      {currentlySelectedGroupChildrenIds.length ? (
                        <div className="child-amount">
                          ({currentlySelectedGroupChildrenIds.length}/{group.values.length}
                          {!!titleGroup && <>&nbsp;{intl.formatMessage({ id: titleGroup })}</>})
                        </div>
                      ) : group.values?.length ? (
                        <div className="child-amount">
                          ({group.values.length}
                          {!!titleGroup && <>&nbsp;{intl.formatMessage({ id: titleGroup })}</>})
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  )}
                  {group.groupDisabled && (
                    <PopupInfo
                      text={disabledTooltip}
                      position="top center"
                      className="parent-filter-disabled-popup"
                    />
                  )}
                </div>
              </div>
              {isExpanded && (
                <div className="expandable-container">
                  {filteredChildren.map((val) => {
                    const isParentChecked = currentParentFilter.includes(group.groupId)
                    const isChecked = currentChildrenFilter.includes(val.id)

                    const toggleFilter = () => {
                      if (isChecked || isParentChecked) {
                        let childFilter: IPageFilter = {
                          filterKey: filterKeyChild,
                          filterValues: [val.id],
                          action: 'remove'
                        }

                        if (isParentChecked) {
                          const newGroupChildrenIds = childrenIds.filter(
                            (childId) => childId !== val.id
                          )
                          const newChildren = [...currentChildrenFilter, ...newGroupChildrenIds]

                          childFilter = {
                            filterKey: filterKeyChild,
                            filterValues: newChildren,
                            action: newChildren.length > 0 ? 'replace' : 'remove'
                          }
                        }

                        managePageFiltersMultiple({
                          pageName: filterPage,
                          filters: [
                            childFilter,
                            {
                              filterKey: filterKeyParent,
                              filterValues: [group.groupId],
                              action: 'remove'
                            }
                          ]
                        })
                      } else {
                        const allGroupChildrenChecked =
                          currentlySelectedGroupChildrenIds.length + 1 === group.values.length

                        let filters: IPageFilter[] = [
                          {
                            filterKey: filterKeyChild,
                            filterValues: [val.id],
                            action: 'add'
                          }
                        ]
                        if (allGroupChildrenChecked && allowParentSelection) {
                          const newChildren = currentChildrenFilter?.length
                            ? currentChildrenFilter.filter(
                                (urlId) => !currentlySelectedGroupChildrenIds.includes(urlId)
                              )
                            : []
                          filters = [
                            {
                              filterKey: filterKeyParent,
                              filterValues: [group.groupId],
                              action: 'add'
                            },
                            {
                              filterKey: filterKeyChild,
                              filterValues: newChildren,
                              action: newChildren.length > 0 ? 'replace' : 'remove'
                            }
                          ]
                        }

                        managePageFiltersMultiple({ pageName: filterPage, filters })
                      }
                      closeFilters && closeFilters()
                    }

                    return (
                      <CheckboxView
                        disabled={group.groupDisabled}
                        key={val.id}
                        content={val.name || val.id}
                        checked={isChecked || isParentChecked}
                        onChange={toggleFilter}
                      />
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default FilterGrouped
